/*
 * Application Insights JavaScript SDK - Channel, 2.8.16
 * Copyright (c) Microsoft and contributors. All rights reserved.
 */
import dynamicProto from "@microsoft/dynamicproto-js";
import { _throwInternal, getJSON, isArray, isFunction, isObject, objForEachKey } from "@microsoft/applicationinsights-core-js";
import { _DYN_LENGTH, _DYN_PUSH, _DYN_STRINGIFY, _DYN_TO_STRING } from "./__DynamicConstants";
var Serializer = /** @class */function () {
  function Serializer(logger) {
    dynamicProto(Serializer, this, function (_self) {
      /**
       * Serializes the current object to a JSON string.
       */
      _self.serialize = function (input) {
        var output = _serializeObject(input, "root");
        try {
          return getJSON()[_DYN_STRINGIFY /* @min:%2estringify */](output);
        } catch (e) {
          // if serialization fails return an empty string
          _throwInternal(logger, 1 /* eLoggingSeverity.CRITICAL */, 48 /* _eInternalMessageId.CannotSerializeObject */, e && isFunction(e[_DYN_TO_STRING /* @min:%2etoString */]) ? e[_DYN_TO_STRING /* @min:%2etoString */]() : "Error serializing object", null, true);
        }
      };
      function _serializeObject(source, name) {
        var circularReferenceCheck = "__aiCircularRefCheck";
        var output = {};
        if (!source) {
          _throwInternal(logger, 1 /* eLoggingSeverity.CRITICAL */, 48 /* _eInternalMessageId.CannotSerializeObject */, "cannot serialize object because it is null or undefined", {
            name: name
          }, true);
          return output;
        }
        if (source[circularReferenceCheck]) {
          _throwInternal(logger, 2 /* eLoggingSeverity.WARNING */, 50 /* _eInternalMessageId.CircularReferenceDetected */, "Circular reference detected while serializing object", {
            name: name
          }, true);
          return output;
        }
        if (!source.aiDataContract) {
          // special case for measurements/properties/tags
          if (name === "measurements") {
            output = _serializeStringMap(source, "number", name);
          } else if (name === "properties") {
            output = _serializeStringMap(source, "string", name);
          } else if (name === "tags") {
            output = _serializeStringMap(source, "string", name);
          } else if (isArray(source)) {
            output = _serializeArray(source, name);
          } else {
            _throwInternal(logger, 2 /* eLoggingSeverity.WARNING */, 49 /* _eInternalMessageId.CannotSerializeObjectNonSerializable */, "Attempting to serialize an object which does not implement ISerializable", {
              name: name
            }, true);
            try {
              // verify that the object can be stringified
              getJSON()[_DYN_STRINGIFY /* @min:%2estringify */](source);
              output = source;
            } catch (e) {
              // if serialization fails return an empty string
              _throwInternal(logger, 1 /* eLoggingSeverity.CRITICAL */, 48 /* _eInternalMessageId.CannotSerializeObject */, e && isFunction(e[_DYN_TO_STRING /* @min:%2etoString */]) ? e[_DYN_TO_STRING /* @min:%2etoString */]() : "Error serializing object", null, true);
            }
          }
          return output;
        }
        source[circularReferenceCheck] = true;
        objForEachKey(source.aiDataContract, function (field, contract) {
          var isRequired = isFunction(contract) ? contract() & 1 /* FieldType.Required */ : contract & 1 /* FieldType.Required */;
          var isHidden = isFunction(contract) ? contract() & 4 /* FieldType.Hidden */ : contract & 4 /* FieldType.Hidden */;
          var isArray = contract & 2 /* FieldType.Array */;
          var isPresent = source[field] !== undefined;
          var isObj = isObject(source[field]) && source[field] !== null;
          if (isRequired && !isPresent && !isArray) {
            _throwInternal(logger, 1 /* eLoggingSeverity.CRITICAL */, 24 /* _eInternalMessageId.MissingRequiredFieldSpecification */, "Missing required field specification. The field is required but not present on source", {
              field: field,
              name: name
            });
            // If not in debug mode, continue and hope the error is permissible
          } else if (!isHidden) {
            // Don't serialize hidden fields
            var value = void 0;
            if (isObj) {
              if (isArray) {
                // special case; recurse on each object in the source array
                value = _serializeArray(source[field], field);
              } else {
                // recurse on the source object in this field
                value = _serializeObject(source[field], field);
              }
            } else {
              // assign the source field to the output even if undefined or required
              value = source[field];
            }
            // only emit this field if the value is defined
            if (value !== undefined) {
              output[field] = value;
            }
          }
        });
        delete source[circularReferenceCheck];
        return output;
      }
      function _serializeArray(sources, name) {
        var output;
        if (!!sources) {
          if (!isArray(sources)) {
            _throwInternal(logger, 1 /* eLoggingSeverity.CRITICAL */, 54 /* _eInternalMessageId.ItemNotInArray */, "This field was specified as an array in the contract but the item is not an array.\r\n", {
              name: name
            }, true);
          } else {
            output = [];
            for (var i = 0; i < sources[_DYN_LENGTH /* @min:%2elength */]; i++) {
              var source = sources[i];
              var item = _serializeObject(source, name + "[" + i + "]");
              output[_DYN_PUSH /* @min:%2epush */](item);
            }
          }
        }
        return output;
      }
      function _serializeStringMap(map, expectedType, name) {
        var output;
        if (map) {
          output = {};
          objForEachKey(map, function (field, value) {
            if (expectedType === "string") {
              if (value === undefined) {
                output[field] = "undefined";
              } else if (value === null) {
                output[field] = "null";
              } else if (!value[_DYN_TO_STRING /* @min:%2etoString */]) {
                output[field] = "invalid field: toString() is not defined.";
              } else {
                output[field] = value[_DYN_TO_STRING /* @min:%2etoString */]();
              }
            } else if (expectedType === "number") {
              if (value === undefined) {
                output[field] = "undefined";
              } else if (value === null) {
                output[field] = "null";
              } else {
                var num = parseFloat(value);
                if (isNaN(num)) {
                  output[field] = "NaN";
                } else {
                  output[field] = num;
                }
              }
            } else {
              output[field] = "invalid field: " + name + " is of unknown type.";
              _throwInternal(logger, 1 /* eLoggingSeverity.CRITICAL */, output[field], null, true);
            }
          });
        }
        return output;
      }
    });
  }
  // Removed Stub for Serializer.prototype.serialize.
  // This is a workaround for an IE8 bug when using dynamicProto() with classes that don't have any
  // non-dynamic functions or static properties/functions when using uglify-js to minify the resulting code.
  // this will be removed when ES3 support is dropped.
  Serializer.__ieDyn = 1;
  return Serializer;
}();
export { Serializer };
//# sourceMappingURL=Serializer.js.map